import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from '../list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  filterRecordsBySearch,
  filterRecordsByStatus,
  paginateClientRecords,
} from 'src/app/shared/utils/client-list.util';
import {
  catalogStatusBadgeClass,
  catalogStatusLabel,
} from 'src/app/shared/utils/catalog-status-badge.util';
import { EntityCatalog, EntityField, EntityFieldOption, EntityRecord, EntityCatalogToolbarTab } from './entity-catalog.types';
import { SearchableSelectComponent } from '../searchable-select/searchable-select.component';

/**
 * Catalog-driven list + editor for a simple configuration entity.
 *
 * List half uses the shared list-module surface (search, status filter, pagination,
 * status badges, and inline row actions when there are two or fewer.
 */
/**
 * A record value as a facet key. Only primitives can name a facet — an object or array would
 * stringify to "[object Object]" and collapse unrelated rows into one meaningless option.
 */
/** Selects the rows whose facet is blank. Not a value any record can hold, so it cannot clash. */
export const BLANK_FACET = '\u0000blank';

function facetValueOf(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return '';
}

@Component({
  selector: 'app-entity-catalog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
    IconModule,
    SearchableSelectComponent,
  ],
  templateUrl: './entity-catalog.component.html',
})
export class EntityCatalogComponent implements OnChanges {
  @Input({ required: true }) catalog!: EntityCatalog;
  @Input() records: EntityRecord[] = [];
  @Input() loading = false;
  @Input() saving = false;
  @Input() error = '';
  @Input() canCreate = true;
  @Input() canEdit = true;
  @Input() canDelete = false;
  @Input() canRestore = false;
  /** Optional tabs rendered to the right of the add button (e.g. Deleted). */
  @Input() toolbarTabs: EntityCatalogToolbarTab[] = [];
  @Input() activeToolbarTab = '';
  /** When true, omit the internal h2 — parent provides {@link PageSurfaceHeaderComponent}. */
  @Input() hideTitle = false;
  @Input() perPage = 10;
  /** Overrides {@link EntityCatalog.searchPlaceholder}. */
  @Input() searchPlaceholder = '';

  @Output() save = new EventEmitter<{ record: EntityRecord; isNew: boolean }>();
  @Output() delete = new EventEmitter<EntityRecord>();
  @Output() restore = new EventEmitter<EntityRecord>();
  @Output() toolbarTabChange = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  draft: EntityRecord | null = null;
  isNew = false;
  savedNotice = '';

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  currentPage = 1;
  /** Selected value of the optional facet filter; '' means every value. */
  facetValue = '';

  ngOnChanges(changes: SimpleChanges): void {
    const saving = changes['saving'];
    if (saving && !saving.firstChange) {
      const finished = saving.previousValue === true && saving.currentValue === false;
      if (finished && !this.error && this.draft) {
        this.savedNotice = `${this.catalog.singular} ${this.isNew ? 'created' : 'updated'}.`;
        this.draft = null;
      }
    }
    if (changes['records']) {
      this.currentPage = 1;
    }
  }

  get listFields(): EntityField[] {
    return this.catalog.fields.filter((f) => f.inList);
  }

  get columns(): ListModuleColumnDef[] {
    return [
      ...this.listFields.map((f) => ({
        id: f.key,
        header: f.label,
        align: 'left' as const,
      })),
      { id: 'actions', header: 'Actions', align: 'right' as const },
    ];
  }

  get statusField(): EntityField | undefined {
    const key =
      this.catalog.statusFieldKey ??
      (this.catalog.fields.some((f) => f.key === 'status') ? 'status' : undefined) ??
      (this.catalog.fields.some((f) => f.key === 'is_active') ? 'is_active' : undefined);
    return key ? this.catalog.fields.find((f) => f.key === key) : undefined;
  }

  get searchKeys(): string[] {
    return this.listFields.map((f) => f.key);
  }

  get toolbarSearchPlaceholder(): string {
    return (
      this.searchPlaceholder ??
      this.catalog.searchPlaceholder ??
      `Search ${this.catalog.title.toLowerCase()}…`
    );
  }

  get surfaceConfig(): ListModuleSurfaceConfig {
    const statusField = this.statusField;
    return {
      toolbar: {
        search: { placeholder: this.toolbarSearchPlaceholder },
        status: statusField
          ? {
              label: 'Status',
              options: this.statusFilterOptions(statusField),
            }
          : false,
        dateRange: false,
      },
      pagination: true,
    };
  }

  get facetField(): EntityField | undefined {
    const key = this.catalog.facetFieldKey;
    return key ? this.catalog.fields.find((f) => f.key === key) : undefined;
  }

  /** The values this facet actually has in the loaded records, sorted, blanks dropped. */
  get facetOptions(): { value: string; label: string }[] {
    const field = this.facetField;
    if (!field) return [];
    const seen = new Map<string, string>();
    for (const row of this.records) {
      const value = facetValueOf(row[field.key]);
      if (!value) continue;
      if (!seen.has(value)) seen.set(value, value);
    }
    const options = [...seen.values()].sort((a, b) => a.localeCompare(b));
    const allLabel = this.catalog.facetAllLabel ?? `All ${field.label.toLowerCase()}s`;
    const blank =
      this.catalog.facetBlankLabel && this.records.some((row) => !facetValueOf(row[field.key]))
        ? [{ value: BLANK_FACET, label: this.catalog.facetBlankLabel }]
        : [];
    return [
      { value: '', label: allLabel },
      ...blank,
      ...options.map((v) => ({ value: v, label: v })),
    ];
  }

  get filteredRecords(): EntityRecord[] {
    let rows = [...this.records];
    rows = filterRecordsBySearch(rows, this.listFilters.searchQuery, this.searchKeys);
    rows = filterRecordsByStatus(rows, this.listFilters.status, this.statusField?.key ?? null);
    const facetKey = this.facetField?.key;
    if (facetKey && this.facetValue) {
      rows = rows.filter((row) => this.matchesFacet(facetValueOf(row[facetKey])));
    }
    return rows;
  }

  private matchesFacet(value: string): boolean {
    if (this.facetValue === BLANK_FACET) return !value;
    return value === this.facetValue;
  }

  onFacetChange(value: string): void {
    this.facetValue = value;
    // Page 3 of the unfiltered list is usually past the end of the filtered one, which would
    // show an empty table for a filter that does match rows.
    this.currentPage = 1;
  }

  get pagedRecords(): EntityRecord[] {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).pageRows;
  }

  get paginationMeta(): ListModulePaginationMeta {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).meta;
  }

  get emptyMessage(): string {
    if (this.activeToolbarTab === 'deleted') {
      return this.records.length
        ? 'No deleted records match your filters.'
        : `No deleted ${this.catalog.title.toLowerCase()} yet.`;
    }
    return this.records.length
      ? 'No records match your filters.'
      : `No ${this.catalog.title.toLowerCase()} yet.`;
  }

  isLocked(field: EntityField): boolean {
    return !this.isNew && !!field.immutable;
  }

  isStatusColumn(field: EntityField): boolean {
    return field.key === this.statusField?.key;
  }

  statusBadgeClass = catalogStatusBadgeClass;
  statusLabel(value: unknown, field: EntityField): string {
    return catalogStatusLabel(value, field);
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.currentPage = 1;
  }

  onListPageChange(page: number): void {
    this.currentPage = page;
  }

  private statusFilterOptions(field: EntityField): { value: string; label: string }[] {
    if (field.kind === 'boolean') {
      return [
        { value: '', label: 'All status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ];
    }
    const options = this.fieldOptions(field).map((o) => ({
      value: o.value,
      label: catalogStatusLabel(o.value, field),
    }));
    return [{ value: '', label: 'All status' }, ...options];
  }

  private emptyValueFor(field: EntityField): unknown {
    switch (field.kind) {
      case 'boolean':
        return false;
      case 'tags':
        return [];
      default:
        return '';
    }
  }

  startCreate(): void {
    this.savedNotice = '';
    this.isNew = true;
    this.draft = this.catalog.fields.reduce<EntityRecord>((acc, f) => {
      acc[f.key] = this.emptyValueFor(f);
      return acc;
    }, {});
  }

  startEdit(record: EntityRecord): void {
    this.savedNotice = '';
    this.isNew = false;
    this.draft = { ...record };
  }

  cancel(): void {
    this.savedNotice = '';
    this.draft = null;
    this.cancelled.emit();
  }

  submit(): void {
    if (!this.draft) return;
    this.save.emit({ record: this.draft, isNew: this.isNew });
  }

  tagsToText(value: unknown): string {
    return Array.isArray(value) ? value.join(', ') : '';
  }

  onTagsInput(field: EntityField, raw: string): void {
    if (!this.draft) return;
    this.draft[field.key] = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  displayValue(record: EntityRecord, field: EntityField): string {
    const value = record[field.key];
    if (value == null || value === '') return '—';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    return JSON.stringify(value) ?? '—';
  }

  trackByField = (_: number, field: EntityField): string => field.key;

  fieldOptions(field: EntityField): EntityFieldOption[] {
    const raw = field.options ?? [];
    const options = raw.map((item) =>
      typeof item === 'string' ? { value: item, label: item } : { value: item.value, label: item.label },
    );
    const current = this.draft?.[field.key];
    if (typeof current === 'string' && current && !options.some((o) => o.value === current)) {
      return [{ value: current, label: `${current} (saved)` }, ...options];
    }
    return options;
  }
}
